// logicTranslate — wires the Translate panel to the live LD endpoints (spec §4.3).
//
//   translateDetail  → POST /api/logic-decomposer/translate         (generate → review → rewrite)
//   reviewDetail     → POST /api/logic-decomposer/adversarial-review (re-check the shown text)
//
// The "unit" the backend translates is built from the artifact's REAL extracted logic (the same
// rows shown on the Business Logic tab), so what gets translated is the file's actual logic — not a
// placeholder. Both endpoints are LLM-backed: they return real output wherever a model key is
// configured (prod), and surface a clean error otherwise — the caller (TranslateBlock) shows a
// friendly message and never crashes.

import { apiClient } from '../../api';
import type { ArtifactDetail, TranslateTab, Translation } from './types';

/** Server-rendered highlighted HTML → plain text (the raw logic the model should translate). */
function htmlToText(html: string): string {
  return new DOMParser().parseFromString(html, 'text/html').body.textContent || '';
}

const MAX_UNIT_ROWS = 60;
const MAX_UNIT_CHARS = 12000;

// One translate call is generate → adversarial review → (rewrite → review), and claudeService
// drains its queue one request at a time. That runs past the shared client's 5-minute default,
// which surfaced as a translation that "failed" while the server was still producing it.
const TRANSLATE_TIMEOUT_MS = 15 * 60 * 1000;

/** Build the translate unit from the artifact's extracted logic rows.
 *  A real workbook carries thousands of rows, most of them the same formula filled down. Sending
 *  all of them overruns the model's context and the request fails, so the unit is a bounded,
 *  de-duplicated selection: every hardcode first (they carry the business rules), then one row per
 *  distinct formula shape. Coverage is stated on the unit so the output never implies it read more
 *  than it did. */
function detailToUnit(detail: ArtifactDetail) {
  const rows = detail.logic_rows;
  const seen = new Set<string>();
  const picked: { location: string; code: string }[] = [];
  const ordered = [...rows.filter((r) => r.tag === 'HC'), ...rows.filter((r) => r.tag !== 'HC')];
  let chars = 0;
  for (const r of ordered) {
    if (picked.length >= MAX_UNIT_ROWS || chars >= MAX_UNIT_CHARS) break;
    const code = htmlToText(r.code_html).trim();
    if (!code) continue;
    const shape = code.replace(/\d+/g, '#').replace(/\$?[A-Z]{1,3}\$?\d+/g, 'REF');
    if (seen.has(shape)) continue;
    seen.add(shape);
    picked.push({ location: r.location, code });
    chars += code.length + r.location.length + 4;
  }
  const verbatim = picked.map((p) => `' ${p.location}\n${p.code}`).join('\n\n');
  const bounded = picked.length < rows.length;
  return {
    verbatim,
    label: detail.name,
    provenance: detail.name,
    context: bounded
      ? `This unit is a de-duplicated selection of ${picked.length} distinct logic statements out of `
        + `${rows.length} extracted from the workbook (repeated fill-down formulas collapsed to one `
        + `representative each). Translate only what is shown.`
      : undefined,
  };
}

/** Generate a translation for one target from the artifact's real logic. */
export async function translateDetail(
  detail: ArtifactDetail | undefined,
  target: TranslateTab,
  selectedModelId: string | null = null,
): Promise<Translation> {
  if (!detail) throw new Error('No artifact selected.');
  const unit = detailToUnit(detail);
  if (!unit.verbatim) throw new Error('This file has no extracted logic to translate.');
  const res = await apiClient.post(
    '/api/logic-decomposer/translate',
    { unit, target, selectedModelId },
    { timeout: TRANSLATE_TIMEOUT_MS },
  );
  return res.data as Translation;
}

/** Keep a generated translation with its run so a reload does not cost another model run.
 *  Best-effort: a failure here must never lose the translation the user is looking at. */
export async function persistTranslation(
  runId: string,
  projectId: number,
  target: TranslateTab,
  translation: Translation,
): Promise<void> {
  try {
    await apiClient.put(
      `/api/logic-decomposer/runs/${runId}/translations/${target}`,
      { projectId, translation },
    );
  } catch (e) {
    console.warn('[Translate] could not persist translation', { runId, target, error: e });
  }
}

/** Re-run the adversarial review on the SHOWN translation, no regeneration. */
export async function reviewDetail(
  detail: ArtifactDetail | undefined,
  target: TranslateTab,
  current: Translation,
  selectedModelId: string | null = null,
): Promise<Translation> {
  if (!detail) throw new Error('No artifact selected.');
  const unit = detailToUnit(detail);
  const translation = htmlToText(current.translation_html);
  const res = await apiClient.post(
    '/api/logic-decomposer/adversarial-review',
    { unit, target, translation, selectedModelId },
    { timeout: TRANSLATE_TIMEOUT_MS },
  );
  const data = res.data as Partial<Translation>;
  // The review endpoint returns the verdict + the same text; keep the original generation metadata.
  return {
    target,
    translation_html: data.translation_html ?? current.translation_html,
    generated_at: current.generated_at,
    model: current.model,
    needs_human_review: data.needs_human_review,
    review_issues: data.review_issues,
  };
}
